import React, { useCallback, type ReactNode } from 'react';
import { isMacOs } from '@jira/platform__os-detection/src/isMacOs.tsx';
import { Box, Text } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { useActionsCommandPalette } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import { DIALOG_WIDTH } from '../../common/constants.tsx';
import { useShortcutsActions } from '../../controllers/shortcuts-service/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';
import { useIsEmbed } from './utils.tsx';

const CommandPaletteSectionMessage = () => {
	const { formatMessage } = useIntl();
	const isEmbed = useIsEmbed();
	const { open } = useActionsCommandPalette();
	const [, { setShortcutsDialogVisibility }] = useShortcutsActions();

	const onClick = useCallback(() => {
		setShortcutsDialogVisibility(false);
		open();
	}, [open, setShortcutsDialogVisibility]);

	const strong = useCallback(
		(...chunks: React.ReactNode[]) => <Text as="strong">{chunks}</Text>,
		[],
	);

	return (
		<SectionMessage
			data-testid="shortcuts-dialog.ui.modal.section-message"
			appearance={isEmbed ? 'information' : 'discovery'}
			title={isEmbed ? undefined : formatMessage(messages.dotMenuRemovalTitle)}
			actions={[
				...(!isEmbed
					? [
							<SectionMessageAction key="try-now" onClick={onClick}>
								{formatMessage(messages.tryNowButton)}
							</SectionMessageAction>,
						]
					: []),
				<SectionMessageAction
					key="learn-more"
					href="https://community.atlassian.com/t5/Jira-Command-Palette-Beta-Group/We-re-removing-Jira-s-dot-menu-to-make-way-for-a-new-and/ba-p/2754322"
				>
					{formatMessage(messages.learnMoreButton)}
				</SectionMessageAction>,
			]}
		>
			{formatMessage(messages.dotMenuRemovalDescription, {
				strong,
				isMac: isMacOs(),
				isEmbed,
			})}
		</SectionMessage>
	);
};

const ModalFooterContainer = ({ children }: { children: ReactNode }) => (
	<Box padding="space.200">{children}</Box>
);

const ModalFooterWrapper = () => (
	<ModalFooterContainer>
		<CommandPaletteSectionMessage />
	</ModalFooterContainer>
);

const Modal = (props: Props): React.JSX.Element => {
	const { onClose } = props;

	return (
		<ShortcutScope>
			<ModalDialog
				messageId="shortcuts-dialog.ui.modal.modal-dialog"
				messageType="transactional"
				width={`${DIALOG_WIDTH}px`}
				onClose={onClose}
			>
				<ModalFooterWrapper />
			</ModalDialog>
		</ShortcutScope>
	);
};

export default Modal;
